import { createContext, useContext, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ExternalLink, AlertTriangle, Check, X, GitMerge, Pencil, ThumbsUp, ThumbsDown, Star, Ban, Copy, Tag, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { get, post, patch, fmtDate, fmtDT, fmtPrice, useConfig } from "@/lib/api";
import { StatusBadge, WeakBadge, ConfBadge, ProvTag, confLevel, ContradictionBadge } from "@/components/badges";

const Ctx = createContext({ open: () => {} });
export const useEventDrawer = () => useContext(Ctx);

const GROUP_TONE = {
  CULTURE: "bg-fuchsia-50 text-fuchsia-900 border-fuchsia-300 dark:bg-fuchsia-950/40 dark:text-fuchsia-200 dark:border-fuchsia-800",
  SPORT: "bg-orange-50 text-orange-900 border-orange-300 dark:bg-orange-950/40 dark:text-orange-200 dark:border-orange-800",
  VIE_LOCALE: "bg-teal-50 text-teal-900 border-teal-300 dark:bg-teal-950/40 dark:text-teal-200 dark:border-teal-800",
  ASSO: "bg-lime-50 text-lime-900 border-lime-300 dark:bg-lime-950/40 dark:text-lime-200 dark:border-lime-800",
  INSTIT: "bg-sky-50 text-sky-900 border-sky-300 dark:bg-sky-950/40 dark:text-sky-200 dark:border-sky-800",
  ECO: "bg-blue-700 text-white border-blue-800",
};
const TypologyBlock = ({ cats, cfg, manual }) => (
  <div data-testid="drawer-typology" className="flex flex-wrap items-center gap-1.5 pt-1">
    {cats.length === 0 && <span className="text-sm font-semibold text-slate-500">TYPOLOGIE INCONNUE</span>}
    {cats.map((c) => {
      const g = c.split(".")[0];
      return (
        <span key={c} data-testid={`drawer-typology-${c}`} className={`inline-flex items-center gap-1.5 border rounded-[3px] px-2 py-1 text-sm font-semibold ${GROUP_TONE[g] || "bg-slate-100 text-slate-800 border-slate-300 dark:bg-slate-800 dark:text-slate-100 dark:border-slate-600"}`}>
          <span className="font-mono text-[9px] uppercase tracking-wider opacity-70">{cfg?.groups?.[g] || g}</span>
          {c.includes(".") && <span>{cfg?.categories?.[c] || c}</span>}
        </span>
      );
    })}
    <span className="text-[10px] font-mono text-slate-400 ml-1">{manual ? "classification corrigée manuellement" : "classification automatique"}</span>
  </div>
);

export const EventDrawerProvider = ({ children }) => {
  const [id, setId] = useState(null);
  return (
    <Ctx.Provider value={{ open: setId }}>
      {children}
      <Sheet open={!!id} onOpenChange={(o) => !o && setId(null)}>
        <SheetContent data-testid="event-detail-drawer" side="right" aria-describedby={undefined} className="w-full sm:max-w-3xl p-0 overflow-y-auto safe-top safe-bottom">
          {id && <EventDetail id={id} onOpen={setId} />}
        </SheetContent>
      </Sheet>
    </Ctx.Provider>
  );
};

const fmtCollected = (v) => Array.isArray(v) ? (v.join(", ") || "—") : typeof v === "boolean" ? (v ? "Oui" : "Non") : String(v);

const Row = ({ label, value, prov, testId, collected, onRevert }) => {
  const manual = prov?.type === "MANUELLE";
  return (
    <div className={`grid grid-cols-[96px_1fr_auto] sm:grid-cols-[130px_1fr_auto] gap-2 py-1.5 border-b border-slate-100 dark:border-slate-800 text-xs ${manual ? "bg-blue-50/60 dark:bg-blue-950/30" : ""}`} data-testid={testId}>
      <div className="overline !text-[9px] pt-0.5">{label}</div>
      <div>
        <div className={value ? "" : "font-mono text-[11px] text-slate-400"}>{value || "INCONNU"}</div>
        {manual && (collected || []).map((c) => (
          <div key={c.source_id} data-testid={testId ? `${testId}-collected` : undefined} className="text-[10px] text-slate-500 leading-tight mt-0.5">
            Collecte auto : <span className="font-mono">{fmtCollected(c.value)}</span> — {c.source_name}
          </div>
        ))}
        {manual && onRevert && (
          <button type="button" data-testid={testId ? `${testId}-revert` : undefined} onClick={onRevert}
            className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-blue-700 dark:text-blue-400 hover:underline">
            <Undo2 className="h-3 w-3" />{(collected || []).length ? "Revenir à la valeur collectée" : "Annuler la correction (valeur inconnue)"}
          </button>
        )}
      </div>
      <div>{value || manual ? <ProvTag prov={prov} /> : null}</div>
    </div>
  );
};

const Link = ({ href }) => href ? <a href={href} target="_blank" rel="noreferrer" className="text-blue-700 dark:text-blue-400 hover:underline break-all inline-flex items-center gap-1">{href}<ExternalLink className="h-3 w-3 shrink-0" /></a> : null;

const ScoreList = ({ title, value, items, extra }) => (
  <div className="panel p-3">
    <div className="flex items-baseline justify-between mb-2">
      <div className="overline">{title}</div>
      <div className="font-mono text-2xl font-semibold tabular-nums">{value}<span className="text-xs text-slate-400">/100</span></div>
    </div>
    {extra}
    <ul className="space-y-1 mt-2">
      {(items || []).map((i, k) => (
        <li key={k} className="flex justify-between text-xs">
          <span>{i.label}</span>
          <span className={`font-mono tabular-nums ${i.points < 0 ? "text-rose-600" : i.points === 0 ? "text-slate-400" : "text-emerald-700 dark:text-emerald-400"}`}>{i.points > 0 ? "+" : ""}{i.points}</span>
        </li>
      ))}
    </ul>
  </div>
);

const FEEDBACK = [
  ["interessant", "Intéressant", ThumbsUp], ["peu_interessant", "Peu intéressant", ThumbsDown], ["important", "Important", Star],
  ["faux_positif", "Faux positif", Ban], ["doublon", "Doublon", Copy], ["mauvaise_categorie", "Mauvaise catégorie", Tag],
];

const EDITABLE = [["title", "Titre"], ["start_date", "Date début (AAAA-MM-JJ)"], ["start_time", "Heure début (HH:MM)"], ["end_date", "Date fin (AAAA-MM-JJ)"], ["end_time", "Heure fin (HH:MM)"], ["venue_name", "Lieu"], ["address", "Adresse"], ["postal_code", "Code postal"], ["organizer_name", "Organisateur"], ["price_min", "Tarif min (€)"], ["price_max", "Tarif max (€)"], ["url_official", "URL officielle"], ["url_organizer", "URL organisateur"], ["url_registration", "URL inscription"], ["url_ticketing", "URL billetterie"]];
const SELECTS = [
  ["access", "Accès", [["", "Inconnu"], ["gratuit", "Gratuit"], ["payant", "Payant"]]],
  ["booking", "Réservation", [["", "Inconnue"], ["obligatoire", "Obligatoire"], ["conseillee", "Conseillée"], ["sans", "Sans réservation"]]],
  ["is_professional", "Public", [["false", "Grand public"], ["true", "Professionnel"]]],
  ["manual_status", "Statut forcé", [["", "Automatique"], ...["CONFIRMÉ", "PROBABLE", "À VÉRIFIER", "MODIFIÉ", "REPORTÉ", "ANNULÉ", "COMPLET", "TERMINÉ"].map((x) => [x, x])]],
];
const sel = "w-full h-7 border border-slate-200 dark:border-slate-700 rounded-[3px] bg-transparent text-xs mt-0.5";

const Chips = ({ options, value, onChange, prefix }) => (
  <div className="flex flex-wrap gap-1 max-h-40 overflow-y-auto">
    {Object.entries(options || {}).map(([code, label]) => (
      <button key={code} type="button" data-testid={`${prefix}-${code}`} onClick={() => onChange(value.includes(code) ? value.filter((c) => c !== code) : [...value, code])}
        className={`text-[10px] px-1.5 py-0.5 rounded-[3px] border transition-colors duration-150 ${value.includes(code) ? "bg-slate-900 text-white border-slate-900 dark:bg-slate-100 dark:text-slate-900" : "border-slate-200 dark:border-slate-700"}`}>{label}</button>
    ))}
  </div>
);

function EditForm({ e, onSaved }) {
  const { data: cfg } = useConfig();
  const [form, setForm] = useState({});
  const [cats, setCats] = useState(e.categories || []);
  const [auds, setAuds] = useState(e.audiences || []);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    const body = { ...form };
    ["price_min", "price_max"].forEach((k) => { if (body[k] !== undefined) body[k] = body[k] === "" ? null : parseFloat(String(body[k]).replace(",", ".")); });
    if (body.is_professional !== undefined) body.is_professional = body.is_professional === "true";
    if (JSON.stringify(cats) !== JSON.stringify(e.categories || [])) body.categories = cats;
    if (JSON.stringify(auds) !== JSON.stringify(e.audiences || [])) body.audiences = auds;
    if (!Object.keys(body).length) return toast.info("Aucune modification");
    try {
      await patch(`/events/${e.id}`, body);
      toast.success("Modification manuelle enregistrée — elle restera prioritaire sur les collectes suivantes");
      setForm({});
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="space-y-3" data-testid="edit-form">
      <p className="text-xs text-slate-500">Les valeurs saisies sont forcées : elles s'affichent en principal, avec la mention « Corrigée manuellement », et ne sont jamais écrasées. Les valeurs trouvées ensuite par la collecte apparaissent en petit à côté, à titre informatif. Videz un champ pour le forcer à « inconnu ». Les liens des sources (onglet Sources) restent en lecture seule.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {EDITABLE.map(([k, label]) => (
          <label key={k} className="text-[11px] text-slate-500">{label}
            <Input data-testid={`edit-${k}`} className="h-7 text-xs mt-0.5" defaultValue={e[k] ?? ""} onChange={(ev) => set(k, ev.target.value)} />
          </label>
        ))}
        {SELECTS.map(([k, label, opts]) => (
          <label key={k} className="text-[11px] text-slate-500">{label}
            <select data-testid={`edit-${k}`} className={sel} defaultValue={String(e[k] ?? "")} onChange={(ev) => set(k, ev.target.value)}>
              {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        ))}
      </div>
      <label className="block text-[11px] text-slate-500">Description
        <Textarea data-testid="edit-description" rows={4} className="text-xs mt-0.5" defaultValue={e.description ?? ""} onChange={(ev) => set("description", ev.target.value)} />
      </label>
      <div><div className="overline mb-1">Typologie</div><Chips options={cfg?.categories} value={cats} onChange={setCats} prefix="edit-cat" /></div>
      <div><div className="overline mb-1">Public visé</div><Chips options={cfg?.audiences} value={auds} onChange={setAuds} prefix="edit-aud" /></div>
      <Button data-testid="edit-save-btn" size="sm" onClick={save}>Enregistrer la modification manuelle</Button>
    </div>
  );
}

function MergeBox({ e, onDone }) {
  const { data } = useQuery({ queryKey: ["dups", e.id], queryFn: () => get(`/events/${e.id}/duplicates`) });
  const merge = async (dupId) => {
    await post(`/events/${e.id}/merge`, { duplicate_id: dupId });
    toast.success("Doublon fusionné : toutes les sources sont rattachées à cet événement");
    onDone();
  };
  return (
    <div className="space-y-1">
      <div className="overline mb-1">Fusionner un doublon dans cette fiche</div>
      {(data || []).map((c) => (
        <div key={c.id} className="flex items-center justify-between text-xs border-b border-slate-100 dark:border-slate-800 py-1">
          <span className="truncate">{c.title} <span className="text-slate-400">· {fmtDate(c.start_date)} · {c.commune_name}</span></span>
          <span className="flex items-center gap-2 shrink-0"><span className="font-mono text-[10px]">{c.similarity}%</span>
            <Button data-testid={`merge-${c.id}`} size="sm" variant="outline" className="h-6 text-[10px]" onClick={() => merge(c.id)}><GitMerge className="h-3 w-3 mr-1" />Fusionner</Button></span>
        </div>
      ))}
    </div>
  );
}

function EventDetail({ id, onOpen }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const { data: cfg } = useConfig();
  const { data: e, refetch } = useQuery({ queryKey: ["event", id], queryFn: () => get(`/events/${id}`) });
  if (!e) return <div className="p-6 text-xs text-slate-500">Chargement…</div>;
  const refresh = () => { refetch(); qc.invalidateQueries({ queryKey: ["events"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); };
  const act = async (path, msg) => { await post(`/events/${id}/${path}`); toast.success(msg); refresh(); };
  const fb = async (kind) => { await post(`/events/${id}/feedback`, { kind }); refresh(); };
  const open = (e.contradictions || []).filter((c) => !c.resolved);
  const p = e.prov || {};
  const cat = (c) => cfg?.categories?.[c] || c;
  const aud = (a) => cfg?.audiences?.[a] || a;
  const cv = e.collected_values || {};
  const revert = async (fields) => {
    try {
      await post(`/events/${e.id}/revert`, { fields });
      toast.success("Correction annulée — la valeur collectée automatiquement est rétablie");
      refresh();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const BOOK = { obligatoire: "Réservation obligatoire", conseillee: "Réservation conseillée", sans: "Sans réservation" };

  return (
    <div>
      <SheetHeader className="p-5 border-b border-slate-200 dark:border-slate-800 space-y-2 text-left">
        <div className="flex flex-wrap items-center gap-1.5">
          <StatusBadge status={e.status} testId="drawer-status" />
          {e.weak_signal && <WeakBadge />}
          {e.has_contradiction && <ContradictionBadge />}
          {(e.locked_fields || []).length > 0 && (
            <span data-testid="manual-edit-badge" title={`Champs corrigés : ${e.locked_fields.join(", ")}`} className="inline-flex items-center gap-1 text-[10px] font-mono font-bold uppercase tracking-wider bg-blue-700 text-white rounded-[3px] px-1.5 py-0.5">
              <Pencil className="h-3 w-3" />Modifié manuellement · {e.locked_fields.length} champ(s)
            </span>
          )}
          {(e.locked_fields || []).length > 0 && (
            <button data-testid="revert-all-btn" onClick={() => window.confirm("Annuler toutes les corrections manuelles de cette fiche ?") && revert(e.locked_fields)}
              className="inline-flex items-center gap-1 text-[10px] text-blue-700 dark:text-blue-400 hover:underline"><Undo2 className="h-3 w-3" />Tout annuler</button>
          )}
          {e.validated && <span className="text-[10px] font-mono text-emerald-700">✓ VALIDÉ</span>}
          {e.is_demo && <span className="text-[10px] font-mono bg-fuchsia-600 text-white px-1">DONNÉES DE DÉMONSTRATION</span>}
        </div>
        <TypologyBlock cats={e.categories || []} cfg={cfg} manual={p.categories?.type === "MANUELLE"} />
        <SheetTitle data-testid="drawer-title" className="font-display text-xl leading-tight">{e.title}</SheetTitle>
        <SheetDescription className="text-xs">
          {fmtDate(e.start_date)}{e.end_date !== e.start_date && ` → ${fmtDate(e.end_date)}`} · {e.start_time || "HEURE INCONNUE"} · {e.commune_name} <span className="font-mono">({e.insee})</span>
        </SheetDescription>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
          <div data-testid="drawer-public" className={`border rounded-[3px] px-2.5 py-1.5 ${e.is_professional ? "border-blue-700 bg-blue-50 dark:bg-blue-950/40" : "border-slate-300 dark:border-slate-600"}`}>
            <div className="overline">Public</div>
            <div className={`text-sm font-bold ${e.is_professional ? "text-blue-800 dark:text-blue-300" : ""}`}>{e.is_professional ? "PROFESSIONNEL" : "GRAND PUBLIC"}</div>
            <div className="text-[11px] text-slate-600 dark:text-slate-400">{(e.audiences || []).length ? e.audiences.map(aud).join(" · ") : "Public précis inconnu"}</div>
          </div>
          <div data-testid="drawer-organizer" className="border border-slate-300 dark:border-slate-600 rounded-[3px] px-2.5 py-1.5">
            <div className="overline flex items-center gap-1.5">Organisateur <ProvTag prov={p.organizer_name} /></div>
            {e.organizer_name
              ? <button data-testid="drawer-organizer-link" className="text-sm font-bold text-left hover:underline" onClick={() => { onOpen(null); nav(`/organisateurs?id=${e.organizer_id}`); }}>{e.organizer_name}</button>
              : <div className="text-sm font-bold text-slate-500">INCONNU</div>}
            {p.organizer_name?.type === "DÉDUITE" && <div className="text-[10px] text-amber-700 dark:text-amber-400">Déduit de l'éditeur de l'agenda source — à confirmer</div>}
          </div>
        </div>
        <div className="flex gap-4 pt-1">
          <div data-testid="drawer-confidence"><div className="overline">Confiance</div><div className="font-mono text-lg font-semibold">{e.confidence}<span className="text-[11px] text-slate-400"> · {confLevel(e.confidence)}</span></div></div>
          <div data-testid="drawer-interest"><div className="overline">Intérêt territorial</div><div className="font-mono text-lg font-semibold">{e.interest}<span className="text-[11px] text-slate-400">/100</span></div></div>
          <div><div className="overline">Sources</div><div className="font-mono text-lg font-semibold">{e.sources_count}</div></div>
        </div>
      </SheetHeader>

      {open.length > 0 && (
        <div data-testid="contradiction-alert" className="mx-5 mt-4 rounded-md border border-rose-300 bg-rose-50 dark:bg-rose-950/50 dark:border-rose-800 p-3 text-rose-900 dark:text-rose-200">
          <div className="flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-wider"><AlertTriangle className="h-4 w-4" />Information contradictoire</div>
          {open.map((c) => (
            <div key={c.field} className="mt-2 text-xs">
              <b>{c.field}</b> — {c.resolution}
              <ul className="mt-1 space-y-0.5">{c.values.map((v) => <li key={v.source_id}>« {String(v.value)} » <span className="opacity-70">— {v.source_name}</span></li>)}</ul>
            </div>
          ))}
        </div>
      )}

      <Tabs defaultValue="fiche" className="p-5">
        <TabsList className="h-8 w-full sm:w-auto justify-start overflow-x-auto">
          <TabsTrigger data-testid="tab-fiche" value="fiche" className="text-xs">Fiche</TabsTrigger>
          <TabsTrigger data-testid="tab-sources" value="sources" className="text-xs">Sources ({e.sources.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-history" value="history" className="text-xs">Historique ({e.history.length})</TabsTrigger>
          <TabsTrigger data-testid="tab-scores" value="scores" className="text-xs">Scores</TabsTrigger>
          <TabsTrigger data-testid="tab-admin" value="admin" className="text-xs">Administration</TabsTrigger>
        </TabsList>

        <TabsContent value="fiche" className="mt-4">
          {p.title?.type === "MANUELLE" && (cv.title || []).map((c) => <div key={c.source_id} data-testid="title-collected" className="text-[10px] text-slate-500 mb-1">Titre collecté auto : « {c.value} » — {c.source_name}</div>)}
          {p.title?.type === "MANUELLE" && (cv.title || []).length > 0 && <button data-testid="title-revert" onClick={() => revert(["title"])} className="text-[10px] text-blue-700 dark:text-blue-400 hover:underline mb-1 mr-3">↺ Revenir au titre collecté</button>}
          {p.description?.type === "MANUELLE" && <div className="text-[10px] font-mono text-blue-700 mb-1">DESCRIPTION CORRIGÉE MANUELLEMENT{(cv.description || []).length ? ` — ${cv.description.length} version(s) collectée(s) disponible(s) dans l'historique` : ""} <button data-testid="description-revert" onClick={() => revert(["description"])} className="ml-2 font-sans text-blue-700 dark:text-blue-400 hover:underline">↺ Revenir à la description collectée</button></div>}
          {e.description && <p className="text-xs leading-relaxed whitespace-pre-line mb-4 max-h-48 overflow-y-auto text-slate-700 dark:text-slate-300">{e.description}</p>}
          <Row label="Date début" value={fmtDate(e.start_date)} prov={p.start_date} testId="field-start-date" collected={cv.start_date} onRevert={() => revert(["start_date"])} />
          <Row label="Heure début" value={e.start_time} prov={p.start_time} testId="field-start-time" collected={cv.start_time} onRevert={() => revert(["start_time"])} />
          <Row label="Date fin" value={e.end_date && fmtDate(e.end_date)} prov={p.end_date} collected={cv.end_date} onRevert={() => revert(["end_date"])} testId="field-end-date" />
          <Row label="Heure fin" value={e.end_time} prov={p.end_time} collected={cv.end_time} onRevert={() => revert(["end_time"])} testId="field-end-time" />
          {Object.keys(e.time_variants || {}).length > 0 && (
            <div data-testid="time-variants" className="col-span-full text-[11px] border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 rounded-[3px] px-2 py-1.5">
              <div className="font-semibold">Horaires variables selon les sources <span className="font-normal text-slate-500">— écart habituel pour une brocante / vide-grenier, non signalé comme contradiction</span></div>
              {Object.entries(e.time_variants).map(([f, vs]) => (
                <div key={f} className="font-mono text-[10px] text-slate-600 dark:text-slate-400">{f === "start_time" ? "Début" : "Fin"} : {vs.map((x) => `${x.value} (${x.source_name})`).join(" · ")}</div>
              ))}
            </div>
          )}
          <Row label="Récurrence" value={e.recurrence ? `${e.recurrence.replace("_", " ")} · ${e.occurrences?.length} séance(s)` : null} />
          <Row label="Lieu" value={e.venue_name && <button className="hover:underline" onClick={() => { onOpen(null); nav(`/lieux?id=${e.venue_id}`); }}>{e.venue_name}</button>} prov={p.venue_name} collected={cv.venue_name} onRevert={() => revert(["venue_name"])} testId="field-venue" />
          <Row label="Adresse" value={e.address} prov={p.address} collected={cv.address} onRevert={() => revert(["address"])} testId="field-address" />
          <Row label="Code postal / INSEE" value={[e.postal_code, e.insee].filter(Boolean).join(" · ")} prov={p.postal_code} collected={cv.postal_code} onRevert={() => revert(["postal_code"])} testId="field-postal-code" />
          <Row label="Coordonnées" value={e.lat ? `${e.lat.toFixed(5)}, ${e.lon.toFixed(5)} (${e.coords_precision || "?"})` : null} prov={p.lat} />
          <Row label="Organisateur" value={e.organizer_name && <button className="hover:underline" onClick={() => { onOpen(null); nav(`/organisateurs?id=${e.organizer_id}`); }}>{e.organizer_name}</button>} prov={p.organizer_name} collected={cv.organizer_name} onRevert={() => revert(["organizer_name"])} testId="field-organizer" />
          <Row label="Accès / tarif" value={e.access || e.price_min != null ? fmtPrice(e) : null} prov={p.access || p.price_min} collected={[...(cv.access || []), ...(cv.price_min || []), ...(cv.price_max || [])]} onRevert={() => revert(["access", "price_min", "price_max"].filter((f) => e.locked_fields?.includes(f)))} testId="field-price" />
          <Row label="Réservation" value={BOOK[e.booking]} prov={p.booking} collected={cv.booking} onRevert={() => revert(["booking"])} testId="field-booking" />
          <Row label="Public" value={(e.audiences || []).map(aud).join(", ")} prov={p.audiences} collected={cv.audiences} onRevert={() => revert(["audiences"])} testId="field-audiences" />
          <Row label="Typologie" value={(e.categories || []).map(cat).join(" · ")} prov={p.categories} collected={(cv.categories || []).map((c) => ({ ...c, value: c.value.map(cat) }))} onRevert={() => revert(["categories"])} testId="field-categories" />
          <Row label="Professionnel" value={e.is_professional ? "Oui — événement professionnel / économique" : "Non"} prov={p.is_professional} collected={cv.is_professional} onRevert={() => revert(["is_professional"])} testId="field-pro" />
          <Row label="URL officielle" value={e.url_official && <Link href={e.url_official} />} prov={p.url_official} collected={cv.url_official} onRevert={() => revert(["url_official"])} testId="field-url-official" />
          <Row label="URL organisateur" value={e.url_organizer && <Link href={e.url_organizer} />} prov={p.url_organizer} collected={cv.url_organizer} onRevert={() => revert(["url_organizer"])} />
          <Row label="Inscription" value={e.url_registration && <Link href={e.url_registration} />} prov={p.url_registration} collected={cv.url_registration} onRevert={() => revert(["url_registration"])} />
          <Row label="Billetterie" value={e.url_ticketing && <Link href={e.url_ticketing} />} prov={p.url_ticketing} collected={cv.url_ticketing} onRevert={() => revert(["url_ticketing"])} />
          <Row label="1ère détection" value={fmtDT(e.first_seen_at)} />
          <Row label="Dernière vérif." value={fmtDT(e.last_verified_at)} />
          {e.occurrences?.length > 1 && (
            <div className="mt-4"><div className="overline mb-1">Occurrences ({e.occurrences.length})</div>
              <div className="flex flex-wrap gap-1">{e.occurrences.slice(0, 60).map((o, i) => <span key={i} className="font-mono text-[10px] border border-slate-200 dark:border-slate-700 rounded-[3px] px-1">{fmtDate(o.start_date, false)} {o.start_time || ""}</span>)}</div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="sources" className="mt-4 space-y-3">
          {e.sources.map((s) => (
            <div key={s.id} className="panel p-3" data-testid={`event-source-${s.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-sm font-semibold">{s.name}</div>
                  <div className="text-[11px] text-slate-500">{s.domain} · <span className="font-mono">rang {s.rank}</span> · {cfg?.authorities?.[s.authority]} · méthode {s.method}</div>
                </div>
                <div className="text-[10px] font-mono text-slate-400 text-right">consultée {fmtDT(s.last_checked_at)}</div>
              </div>
              {s.observations.map((o) => (
                <div key={o.id} className="mt-2 border-t border-slate-100 dark:border-slate-800 pt-2 text-xs space-y-1">
                  <Link href={o.url} />
                  <div className="text-[10px] font-mono text-slate-500">relevé {fmtDT(o.fetched_at)} · extraction : {o.extractor || "—"}</div>
                  {o.excerpt && <blockquote className="border-l-2 border-slate-300 pl-2 italic text-slate-600 dark:text-slate-400 text-[11px]">{o.excerpt}</blockquote>}
                  <details><summary className="cursor-pointer text-[10px] font-mono text-slate-500">Informations extraites</summary>
                    <pre className="text-[10px] font-mono bg-slate-50 dark:bg-slate-950 p-2 rounded overflow-x-auto">{JSON.stringify(o.extracted, null, 1)}</pre></details>
                </div>
              ))}
            </div>
          ))}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <ol className="relative border-l border-slate-200 dark:border-slate-700 ml-2 space-y-3" data-testid="event-history">
            {e.history.map((h) => (
              <li key={h.id} className="ml-4">
                <span className="absolute -left-1 mt-1.5 h-2 w-2 rounded-full bg-slate-400" />
                <div className="font-mono text-[10px] text-slate-500">{fmtDT(h.at)} · {h.kind.replace("_", " ")}</div>
                <div className="text-xs">{h.message}</div>
              </li>
            ))}
          </ol>
        </TabsContent>

        <TabsContent value="scores" className="mt-4 grid md:grid-cols-2 gap-3">
          <ScoreList title="Score de confiance" value={e.confidence} items={e.confidence_explain} extra={<div className="text-[11px] text-slate-500">80-100 ÉLEVÉE · 50-79 MOYENNE · 0-49 À VÉRIFIER</div>} />
          <ScoreList title="Intérêt territorial" value={e.interest} items={e.interest_explain} extra={<div className="text-[11px] text-slate-500">Distinct de la confiance. Signal faible : {e.weak_signal ? "OUI" : "non"}</div>} />
        </TabsContent>

        <TabsContent value="admin" className="mt-4 space-y-5">
          <div className="flex flex-wrap gap-2">
            <Button data-testid="validate-btn" size="sm" onClick={() => act("validate", "Événement validé")}><Check className="h-3.5 w-3.5 mr-1" />Valider</Button>
            {e.rejected ? <Button data-testid="restore-btn" size="sm" variant="outline" onClick={() => act("restore", "Restauré")}>Restaurer</Button>
              : <Button data-testid="reject-btn" size="sm" variant="outline" onClick={() => act("reject", "Événement rejeté (conservé et masqué)")}><X className="h-3.5 w-3.5 mr-1" />Rejeter</Button>}
          </div>
          <div>
            <div className="overline mb-1.5">Retour utilisateur (n'altère jamais les faits sourcés)</div>
            <div className="flex flex-wrap gap-1.5">
              {FEEDBACK.map(([k, label, Icon]) => (
                <Button key={k} data-testid={`feedback-${k}`} size="sm" variant={e.feedback?.[k] ? "default" : "outline"} className="h-7 text-[11px]" onClick={() => fb(k)}><Icon className="h-3 w-3 mr-1" />{label}</Button>
              ))}
            </div>
          </div>
          <MergeBox e={e} onDone={refresh} />
          <div><div className="overline mb-1.5 flex items-center gap-1"><Pencil className="h-3 w-3" />Modifier / forcer les informations</div><EditForm e={e} onSaved={refresh} /></div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
